import {CELL_OUTLINE, CELL_OUTLINE_PALETTES, CELL_OUTLINE_TEXTURE_KEYS} from '../configs/fieldCellOutlines';
import {MELEE_TEXTURE_KEYS} from '../configs/meleeAnimation';
import {CELL_OUTLINE_RADIUS_PX, ensureCellOutlineTextures} from '../utils/effects/cellOutlineTextures';
import {buildThreadGeometry, resolveThreadPoint} from '../utils/effects/glowThreadGeometry';
import {ensureMeleeTextures} from '../utils/effects/meleeEffectTextures';

const TWO_PI = Math.PI * 2;

/**
 * Волна света по сетке доступных ячеек: чем дальше ячейка от источника, тем позже
 * к ней приходит гребень
 * @returns {number} прозрачность ореола
 */
export const resolveWaveAlpha = (now, distance) => {
  const {periodMs, lengthPx, minAlpha, maxAlpha} = CELL_OUTLINE.wave;
  const share = (Math.sin((now / periodMs - distance / lengthPx) * TWO_PI) + 1) / 2;
  return minAlpha + (maxAlpha - minAlpha) * share;
};

/**
 * ОБВОДКИ ЯЧЕЕК (см. configs/fieldCellOutlines). Обводка — контейнер: снаружи она
 * ведет себя как прежняя графика (setDepth, setAlpha, destroy, твин мигания).
 *
 * Поле пересоздает обводки на каждое обновление стора, поэтому свет считается от
 * часов сцены: новая обводка продолжает волну. Обводку убирает ячейка; здесь она
 * просто перестает обновляться.
 */
export default class FieldCellOutlines {
  /**
   * @param {Phaser.Scene} scene
   * @param {Object} [options]
   * @param {number} [options.linearFilter] — режим фильтрации LINEAR для текстур
   */
  constructor(scene, {linearFilter} = {}) {
    this.scene = scene;
    this.linearFilter = linearFilter;
    this.outlines = new Set();
    this.isListening = false;
    this.tick = this.tick.bind(this);
  }

  now() {
    return this.scene?.time?.now ?? 0;
  }

  /**
   * @param {Object} params
   * @param {Object} params.hex — ячейка: centerX, centerY и вершины points
   * @param {Object} [params.palette] — см. CELL_OUTLINE_PALETTES
   * @param {number} params.depth
   * @param {boolean} [params.isDashed] — пунктир: сюда строить можно, но с условием
   * @param {boolean} [params.isSelected] — выбранная ячейка
   * @param {{x: number, y: number}} [params.waveOrigin] — откуда идет волна света
   * @returns {Phaser.GameObjects.Container}
   */
  createOutline({
    hex, palette = CELL_OUTLINE_PALETTES.DEFAULT, depth, isDashed = false, isSelected = false, waveOrigin = {x: 0, y: 0},
  }) {
    ensureCellOutlineTextures(this.scene, {linearFilter: this.linearFilter});
    ensureMeleeTextures(this.scene, {linearFilter: this.linearFilter});

    const relativePoints = hex.points.map((point) => ({x: point.x - hex.centerX, y: point.y - hex.centerY}));
    const [firstPoint] = relativePoints;
    const radius = Math.hypot(firstPoint.x, firstPoint.y);
    const scale = radius / CELL_OUTLINE_RADIUS_PX;
    // Вершина кольца на картинке лежит на оси x: поворот подгоняет ее под вершину ячейки
    const rotation = Math.atan2(firstPoint.y, firstPoint.x);
    const children = [];
    const addRing = (textureKey, tint, ringScale = 1) => {
      const ring = this.scene.add.image(0, 0, textureKey);
      ring.setScale(scale * ringScale);
      ring.setRotation(rotation);
      ring.setTint(tint);
      children.push(ring);
      return ring;
    };

    const shadow = addRing(CELL_OUTLINE_TEXTURE_KEYS.GLOW, CELL_OUTLINE.shadowTint);
    shadow.setAlpha(CELL_OUTLINE.shadowAlpha);
    const outerGlow = isSelected ? addRing(CELL_OUTLINE_TEXTURE_KEYS.GLOW, palette.halo, CELL_OUTLINE.selected.outerScale) : null;
    const glow = addRing(CELL_OUTLINE_TEXTURE_KEYS.GLOW, palette.halo);
    const coreKey = isSelected
      ? CELL_OUTLINE_TEXTURE_KEYS.CORE_THICK
      : (isDashed ? CELL_OUTLINE_TEXTURE_KEYS.CORE_DASHED : CELL_OUTLINE_TEXTURE_KEYS.CORE);
    addRing(coreKey, palette.core);

    let motes = [];
    let perimeter = null;
    if (isSelected) {
      const {count, sizeFactor} = CELL_OUTLINE.selected.motes;
      perimeter = buildThreadGeometry([...relativePoints, firstPoint]);
      motes = Array.from({length: count}, () => {
        const mote = this.scene.add.image(0, 0, MELEE_TEXTURE_KEYS.GLOW);
        mote.setScale((radius * sizeFactor) / (mote.width || 1));
        mote.setTint(palette.mote);
        children.push(mote);
        return mote;
      });
    }

    const container = this.scene.add.container(hex.centerX, hex.centerY, children);
    container.setDepth(isSelected ? depth + CELL_OUTLINE.selected.depthLift : depth);

    const distance = Math.hypot(hex.centerX - waveOrigin.x, hex.centerY - waveOrigin.y);
    const outline = {container, glow, outerGlow, motes, perimeter, distance, isSelected};
    this.outlines.add(outline);
    this.updateOutline(outline, this.now());
    if (!this.isListening && this.scene?.events?.on) {
      this.scene.events.on('update', this.tick);
      this.isListening = true;
    }
    return container;
  }

  updateOutline({glow, outerGlow, motes, perimeter, distance, isSelected}, now) {
    if (!isSelected) {
      glow.setAlpha(resolveWaveAlpha(now, distance));
      return;
    }
    const {pulseMs, minAlpha, maxAlpha, motes: motesConfig} = CELL_OUTLINE.selected;
    const share = (Math.sin((now / pulseMs) * TWO_PI) + 1) / 2;
    glow.setAlpha(minAlpha + (maxAlpha - minAlpha) * share);
    outerGlow.setAlpha(0.35 + 0.45 * share);
    motes.forEach((mote, index) => {
      const point = resolveThreadPoint(perimeter, perimeter.length * ((now / motesConfig.periodMs + index / motes.length) % 1));
      mote.setPosition(point.x, point.y);
    });
  }

  tick() {
    const now = this.now();
    this.outlines.forEach((outline) => {
      if (outline.container.active === false) {
        this.outlines.delete(outline);
        return;
      }
      this.updateOutline(outline, now);
    });
    if (this.outlines.size === 0 && this.isListening) {
      this.scene?.events?.off?.('update', this.tick);
      this.isListening = false;
    }
  }

  /** Забыть все обводки (разбор поля). Контейнеры убирают их ячейки */
  clear() {
    this.outlines.clear();
    if (this.isListening) {
      this.scene?.events?.off?.('update', this.tick);
      this.isListening = false;
    }
  }
}
